import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, ExternalLink, MapPin, Globe, CheckCircle, AlertCircle, ArrowRight, Clock, MessageSquare, Calendar } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import { distributor } from '../data/mock';
import { contactFAQs } from '../data/approvedContent';
import { toast } from 'sonner';
import axios from 'axios';
import GlitchText from '../components/ui/GlitchText';
import { useMotion } from '../context/MotionContext';
import { trackCTAClick, trackFormSubmit, trackPageView } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Timeline steps
const nextSteps = [
  {
    step: 1,
    icon: Send,
    title: 'Submit Inquiry',
    description: 'Fill out the form or contact FMSInc. directly.'
  },
  {
    step: 2,
    icon: MessageSquare,
    title: 'Distributor Contact',
    description: 'FMSInc. reviews your inquiry within 1-2 business days.'
  },
  {
    step: 3,
    icon: Calendar,
    title: 'Consultation',
    description: 'Schedule a demo, receive quotation, or get technical advice.'
  }
];

const ContactPHPage = () => {
  const location = useLocation();
  const prefillProduct = location.state?.productName || '';
  const [formData, setFormData] = useState(() => ({
    name: '',
    email: '',
    organization: '',
    phone: '',
    inquiry_type: prefillProduct ? 'price_quote' : 'product_inquiry',
    customer_type: 'new',
    existing_need: '',
    purpose: '',
    purpose_details: '',
    consent: false,
    message: prefillProduct
      ? `I am writing to request a quotation and more information about the ${prefillProduct}. Kindly get back to me with the details.`
      : '',
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { prefersReducedMotion } = useMotion();

  // Track page view
  useEffect(() => {
    trackPageView('ContactPH');
  }, []);

  // When arriving from a product/system CTA, bring the user straight to the form
  useEffect(() => {
    if (prefillProduct) {
      const t = setTimeout(() => {
        document.getElementById('inquiry-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [prefillProduct]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'customer_type' && value === 'new' ? { existing_need: '' } : {}),
    }));
  };

  const handleFMSClick = () => {
    trackCTAClick('fms', 'ContactPHPage');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      trackFormSubmit('ContactPHForm');
      setIsSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success confirmation view
  if (isSubmitted) {
    return (
      <>
        <SEOHead
          title="Thank You - Contact Philippines"
          description="Your inquiry has been received. FMSInc., the exclusive XION distributor in the Philippines, will contact you shortly."
          path="/contact-ph"
        />
        <Header />
        <main className="pt-32 min-h-screen bg-black grid-bg">
          <div className="max-w-3xl mx-auto px-6 py-16">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#0a0a0a] border border-[#00FFD1]/30 p-8 md:p-12 text-center"
            >
              <div className="w-16 h-16 bg-[#00FFD1]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-[#00FFD1]" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Thank You for Your Inquiry!
              </h2>
              <p className="text-white/60 mb-8 leading-relaxed">
                Your inquiry has been received. For immediate assistance,
                please contact our exclusive Philippine distributor directly:
              </p>

              {/* Distributor Highlight Card */}
              {/* // Distributor exclusivity statement is client-provided; confirm final legal wording before launch. */}
              <div className="bg-black/50 border border-[#00FFD1]/50 p-6 mb-8">
                <p className="terminal-label text-[#00FFD1] mb-2 flex items-center justify-center gap-2">
                  <MapPin size={14} />
                  Exclusive distributor in the Philippines
                </p>
                <h3 className="text-2xl font-semibold text-white mb-2">
                  {distributor.name}
                </h3>
                <a
                  href={distributor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleFMSClick}
                  className="text-[#00FFD1] hover:underline flex items-center justify-center gap-2 text-lg"
                >
                  <Globe size={18} />
                  {distributor.displayUrl}
                  <ExternalLink size={14} />
                </a>
              </div>

              {/* Next Steps */}
              <div className="bg-[#0a0a0a] border border-white/10 p-6 mb-8 text-left">
                <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <ArrowRight size={16} className="text-[#00FFD1]" />
                  What Happens Next
                </h4>
                <ol className="space-y-3 text-white/70 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="text-[#00FFD1] font-mono">01</span>
                    <span>FMSInc. will review your inquiry within 1-2 business days.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#00FFD1] font-mono">02</span>
                    <span>A representative will contact you to discuss your requirements.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#00FFD1] font-mono">03</span>
                    <span>Schedule a product demonstration at your facility if needed.</span>
                  </li>
                </ol>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={distributor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleFMSClick}
                  data-testid="success-visit-fms-btn"
                  className="btn-distributor"
                >
                  Visit FMSInc. Website
                  <ExternalLink size={16} />
                </a>
                <a
                  href="/"
                  className="btn-secondary"
                >
                  Return to Home
                </a>
              </div>
            </motion.div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Contact Philippines"
        description="Contact XION Medical in the Philippines. Submit an inquiry to FMSInc., our exclusive distributor, for product information, demos, and quotations."
        path="/contact-ph"
        includeFAQSchema={true}
      />
      <Header />
      <main className="pt-32 min-h-screen bg-hud">
        {/* Hero Section - Above the Fold */}
        {/* // Distributor exclusivity statement is client-provided; confirm final legal wording before launch. */}
        <section className="py-12 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <p className="terminal-label text-[#00FFD1] mb-4">// Philippines Inquiries</p>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {prefersReducedMotion ? 'Contact (PH)' : <GlitchText text="Contact (PH)" />}
              </h1>
              <p className="text-xl text-white/70 mb-6 max-w-2xl mx-auto">
                <span className="text-[#00FFD1] font-semibold">Exclusive distributor in the Philippines:</span>
                <br />
                {distributor.name}
              </p>

              {/* Primary CTAs - Big Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <a
                  href={distributor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleFMSClick}
                  data-testid="hero-visit-fms-btn"
                  className="btn-distributor text-lg py-4 px-8"
                >
                  Visit FMSInc. Website
                  <ExternalLink size={18} />
                </a>
                <a
                  href="#inquiry-form"
                  className="btn-secondary text-lg py-4 px-8"
                >
                  Send Inquiry
                  <ArrowRight size={18} />
                </a>
              </div>

              <a
                href={distributor.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleFMSClick}
                data-testid="hero-distributor-url-link"
                className="text-[#F08A24] hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                <Globe size={16} />
                {distributor.displayUrl}
              </a>
            </motion.div>
          </div>
        </section>

        {/* What Happens Next Timeline */}
        <section className="py-12 bg-[#0a0a0a]">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="text-2xl font-bold text-white mb-2">What Happens Next</h2>
              <p className="text-white/60">Your path to XION products in the Philippines</p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {nextSteps.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.step}
                    initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 border border-[#00FFD1]/30 flex items-center justify-center mx-auto mb-4 relative">
                      <Icon className="text-[#00FFD1]" size={24} />
                      <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#00FFD1] text-black text-xs font-bold flex items-center justify-center">
                        {item.step}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                    <p className="text-white/60 text-sm">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section id="inquiry-form" className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl font-bold text-white mb-6">Send an Inquiry</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="terminal-label text-[#00FFD1] block mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-[#0a0a0a] border border-white/20 px-4 py-3 text-white focus:border-[#00FFD1] focus:outline-none focus:ring-1 focus:ring-[#00FFD1] transition-colors"
                        placeholder="Dr. Juan dela Cruz"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="terminal-label text-[#00FFD1] block mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-[#0a0a0a] border border-white/20 px-4 py-3 text-white focus:border-[#00FFD1] focus:outline-none focus:ring-1 focus:ring-[#00FFD1] transition-colors"
                        placeholder="doctor@hospital.ph"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="organization" className="terminal-label text-[#00FFD1] block mb-2">
                        Organization / Hospital
                      </label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        className="w-full bg-[#0a0a0a] border border-white/20 px-4 py-3 text-white focus:border-[#00FFD1] focus:outline-none focus:ring-1 focus:ring-[#00FFD1] transition-colors"
                        placeholder="Manila Medical Center"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="terminal-label text-[#00FFD1] block mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-[#0a0a0a] border border-white/20 px-4 py-3 text-white focus:border-[#00FFD1] focus:outline-none focus:ring-1 focus:ring-[#00FFD1] transition-colors"
                        placeholder="+63 917 123 4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="inquiry_type" className="terminal-label text-[#00FFD1] block mb-2">
                      Inquiry Type *
                    </label>
                    <select
                      id="inquiry_type"
                      name="inquiry_type"
                      value={formData.inquiry_type}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#0a0a0a] border border-white/20 px-4 py-3 text-white focus:border-[#00FFD1] focus:outline-none focus:ring-1 focus:ring-[#00FFD1] transition-colors"
                    >
                      <option value="product_inquiry">Product Inquiry</option>
                      <option value="demo_request">Demo Request</option>
                      <option value="price_quote">Price Quote</option>
                      <option value="service_support">Service & Support</option>
                      <option value="training">Training Programs</option>
                      <option value="partnership">Partnership Opportunity</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Purpose of inquiry */}
                  <div>
                    <label htmlFor="purpose" className="terminal-label text-[#00FFD1] block mb-2">
                      For what purpose? *
                    </label>
                    <select
                      id="purpose"
                      name="purpose"
                      value={formData.purpose}
                      onChange={handleChange}
                      required
                      data-testid="inquiry-purpose-select"
                      className="w-full bg-[#0a0a0a] border border-white/20 px-4 py-3 text-white focus:border-[#00FFD1] focus:outline-none focus:ring-1 focus:ring-[#00FFD1] transition-colors"
                    >
                      <option value="" disabled>Select a purpose...</option>
                      <option value="new_purchase">New purchase</option>
                      <option value="replacement">Replacement of existing equipment</option>
                      <option value="upgrade">Upgrade / expansion</option>
                      <option value="clinical_demo">Clinical demo / trial</option>
                      <option value="budget_planning">Budget / procurement planning</option>
                      <option value="research_education">Research / education</option>
                      <option value="other">Other</option>
                    </select>
                    <div className="mt-3">
                      <textarea
                        id="purpose_details"
                        name="purpose_details"
                        value={formData.purpose_details}
                        onChange={handleChange}
                        rows={2}
                        data-testid="inquiry-purpose-details"
                        className="w-full bg-[#0a0a0a] border border-white/20 px-4 py-3 text-white focus:border-[#00FFD1] focus:outline-none focus:ring-1 focus:ring-[#00FFD1] transition-colors resize-none text-sm"
                        placeholder="Additional details about your purpose (optional)"
                      />
                    </div>
                  </div>

                  {/* Customer type */}
                  <div>
                    <label className="terminal-label text-[#00FFD1] block mb-2">
                      Are you a new or existing customer? *
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-testid="customer-type-group">
                      {[
                        { value: 'new', label: 'New customer' },
                        { value: 'existing', label: 'Existing customer' },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          data-testid={`customer-type-${opt.value}`}
                          className={`cursor-pointer border px-4 py-3 text-sm text-center transition-colors ${
                            formData.customer_type === opt.value
                              ? 'border-[#00FFD1] bg-[#00FFD1]/10 text-[#00FFD1]'
                              : 'border-white/20 text-white/70 hover:border-white/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name="customer_type"
                            value={opt.value}
                            checked={formData.customer_type === opt.value}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Existing customer follow-up */}
                  {formData.customer_type === 'existing' && (
                    <div data-testid="existing-need-block">
                      <label className="terminal-label text-[#00FFD1] block mb-2">
                        How can we help with your current system? *
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { value: 'service', label: 'Service for my current system' },
                          { value: 'upgrade', label: 'Planning an upgrade' },
                        ].map((opt) => (
                          <label
                            key={opt.value}
                            data-testid={`existing-need-${opt.value}`}
                            className={`cursor-pointer border px-4 py-3 text-sm text-center transition-colors ${
                              formData.existing_need === opt.value
                                ? 'border-[#00FFD1] bg-[#00FFD1]/10 text-[#00FFD1]'
                                : 'border-white/20 text-white/70 hover:border-white/40'
                            }`}
                          >
                            <input
                              type="radio"
                              name="existing_need"
                              value={opt.value}
                              checked={formData.existing_need === opt.value}
                              onChange={handleChange}
                              required
                              className="sr-only"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="message" className="terminal-label text-[#00FFD1] block mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full bg-[#0a0a0a] border border-white/20 px-4 py-3 text-white focus:border-[#00FFD1] focus:outline-none focus:ring-1 focus:ring-[#00FFD1] transition-colors resize-none"
                      placeholder="Please describe your inquiry or requirements..."
                    />
                  </div>

                  {/* Data Privacy Act (RA 10173) consent — required, unchecked by default */}
                  <div className="flex items-start gap-3 bg-[#0a0a0a] border border-white/15 rounded-md p-4">
                    <input
                      type="checkbox"
                      id="consent"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleChange}
                      required
                      data-testid="inquiry-consent-checkbox"
                      className="mt-1 w-4 h-4 accent-[#00FFD1] shrink-0 cursor-pointer"
                    />
                    <label htmlFor="consent" className="text-white/70 text-xs leading-relaxed cursor-pointer">
                      I have read the{' '}
                      <Link to="/privacy-policy" target="_blank" className="text-[#00FFD1] hover:underline" data-testid="inquiry-privacy-link">
                        Privacy Notice
                      </Link>{' '}
                      and consent to Fortschritt Medical Supplies Inc. (FMSInc.) collecting and processing my
                      personal data (name, email, phone and organization) for the sole purpose of responding to
                      this inquiry, in accordance with the Data Privacy Act of 2012 (Republic Act No. 10173).
                      I understand my consent is voluntary and that I may access, correct, or withdraw it anytime
                      by emailing{' '}
                      <a href="mailto:fms.medequip@gmail.com" className="text-[#00FFD1] hover:underline">fms.medequip@gmail.com</a>. *
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.consent}
                    className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Inquiry
                        <Send size={16} />
                      </>
                    )}
                  </button>
                </form>
              </motion.div>

              {/* Sidebar */}
              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                {/* Main Distributor Card */}
                {/* // Distributor exclusivity statement is client-provided; confirm final legal wording before launch. */}
                <div className="bg-gradient-to-br from-[#F08A24]/10 to-transparent border-2 border-[#F08A24]/40 p-8">
                  <p className="terminal-label text-[#F08A24] mb-3 flex items-center gap-2">
                    <MapPin size={14} />
                    Exclusive distributor in the Philippines
                  </p>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {distributor.name}
                  </h3>
                  <a
                    href={distributor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleFMSClick}
                    data-testid="sidebar-distributor-url-link"
                    className="text-[#F08A24] hover:text-white transition-colors flex items-center gap-2 text-lg mb-6"
                  >
                    <Globe size={18} />
                    {distributor.displayUrl}
                    <ExternalLink size={14} />
                  </a>
                  <a
                    href={distributor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleFMSClick}
                    data-testid="sidebar-visit-fms-btn"
                    className="btn-distributor w-full justify-center"
                  >
                    Visit FMSInc. Website
                    <ExternalLink size={16} />
                  </a>
                </div>

                {/* Response Time */}
                <div className="bg-[#0a0a0a] border border-white/10 p-6 flex items-start gap-4">
                  <Clock className="text-[#00FFD1] shrink-0" size={24} />
                  <div>
                    <h4 className="text-white font-semibold mb-1">Response Time</h4>
                    <p className="text-white/60 text-sm">
                      FMSInc. typically responds to inquiries within 1-2 business days.
                    </p>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-[#0a0a0a] border border-white/10 p-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-[#00FFD1] shrink-0 mt-0.5" size={20} />
                    <div>
                      <h4 className="text-white font-semibold mb-2">Important Note</h4>
                      <p className="text-white/60 text-sm leading-relaxed">
                        All inquiries are handled by {distributor.shortName}, the exclusive 
                        authorized distributor of XION products in the Philippines.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0a0a0a] border border-white/10 p-4 text-center">
                    <div className="w-2 h-2 bg-[#00FFD1] rounded-full mx-auto mb-2" />
                    <p className="text-white/60 text-sm">Authorized Distributor</p>
                  </div>
                  <div className="bg-[#0a0a0a] border border-white/10 p-4 text-center">
                    <div className="w-2 h-2 bg-[#00FFD1] rounded-full mx-auto mb-2" />
                    <p className="text-white/60 text-sm">Genuine Products</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-[#0a0a0a]">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="text-2xl font-bold text-white mb-2">Frequently Asked Questions</h2>
              <p className="text-white/60">Common questions about XION products in the Philippines</p>
            </motion.div>

            <div className="space-y-4">
              {contactFAQs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="border border-white/10 p-6"
                >
                  <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ContactPHPage;
